/**
 * Form Repository
 * 
 * Data access layer for forms and form submissions
 * - CRUD operations
 * - Supabase integration
 * - Offline sync support
 * - Query optimization
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { Form, FormSubmission, FormSubmissionFilters } from '../../../types/database';

export class FormRepository extends BaseRepository<Form> {
  protected db = db;
  
  constructor() {
    super(db.forms, 'forms');
  }

  /**
   * Get forms by organization
   */
  async getByOrganization(organizationId: string): Promise<Form[]> {
    return this.db.forms
      .where('organizationId')
      .equals(organizationId)
      .sortBy('sortOrder');
  }

  /**
   * Get forms by ship
   */
  async getByShip(vesselId: string): Promise<Form[]> {
    return this.db.forms
      .where('vesselId')
      .equals(vesselId)
      .sortBy('sortOrder');
  }

  /**
   * Get forms by category
   */
  async getByCategory(category: string): Promise<Form[]> {
    return this.db.forms
      .where('category')
      .equals(category)
      .sortBy('sortOrder');
  }

  /**
   * Get forms available to all ships (shipId is null)
   */
  async getGlobalForms(organizationId: string): Promise<Form[]> {
    const allForms = await this.getByOrganization(organizationId);
    return allForms.filter(form => !form.vesselId);
  }

  /**
   * Search forms by name
   */
  async searchByName(searchTerm: string): Promise<Form[]> {
    const term = searchTerm.toLowerCase();
    const allForms = await this.getAll();
    
    return allForms.filter(form =>
      form.formName.toLowerCase().includes(term)
    );
  }

  /**
   * Get forms hierarchy (with parent-child relationships)
   */
  async getFormsHierarchy(organizationId: string): Promise<Form[]> {
    const forms = await this.getByOrganization(organizationId);
    
    // Sort by level and sort_order for hierarchical display
    return forms.sort((a, b) => {
      if (a.level !== b.level) return a.level - b.level;
      return a.sortOrder - b.sortOrder;
    });
  }

  /**
   * Get child forms
   */
  async getChildForms(parentId: number): Promise<Form[]> {
    const allForms = await this.getAll();
    return allForms.filter(form => form.parentId === parentId);
  }

  /**
   * Clone form for another ship
   */
  async cloneToShip(formId: string, newVesselId: string): Promise<Form> {
    const original = await this.getById(formId);
    if (!original) {
      throw new Error('Form not found');
    }

    const cloned: Form = {
      ...original,
      id: crypto.randomUUID(),
      vesselId: newVesselId,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return this.create(cloned);
  }

  /**
   * Update form sort order
   */
  async updateSortOrder(formId: string, newSortOrder: number): Promise<void> {
    await this.update(formId, { sortOrder: newSortOrder });
  }

  /**
   * Get forms statistics
   */
  async getStatistics(organizationId: string) {
    const forms = await this.getByOrganization(organizationId);
    
    const byCategory: Record<string, number> = {};
    forms.forEach(form => {
      const cat = form.category || 'Uncategorized';
      byCategory[cat] = (byCategory[cat] || 0) + 1;
    });

    const totalForms = forms.length;
    const globalForms = forms.filter(f => !f.vesselId).length;
    const shipSpecificForms = totalForms - globalForms;
    const protectedForms = forms.filter(f => f.isProtected).length;

    return {
      totalForms,
      globalForms,
      shipSpecificForms,
      protectedForms,
      byCategory
    };
  }
}

export class FormSubmissionRepository extends BaseRepository<FormSubmission> {
  protected db = db;
  
  constructor() {
    super(db.form_submissions, 'form_submissions');
  }

  /**
   * Get submissions by organization
   */
  async getByOrganization(organizationId: string): Promise<FormSubmission[]> {
    return this.db.form_submissions
      .where('organizationId')
      .equals(organizationId)
      .reverse()
      .sortBy('submittedAt');
  }

  /**
   * Get submissions by ship
   */
  async getByShip(vesselId: string): Promise<FormSubmission[]> {
    return this.db.form_submissions
      .where('vesselId')
      .equals(vesselId)
      .reverse()
      .sortBy('submittedAt');
  }

  /**
   * Get submissions by user
   */
  async getByUser(userId: string): Promise<FormSubmission[]> {
    return this.db.form_submissions
      .where('submittedBy')
      .equals(userId)
      .reverse()
      .sortBy('submittedAt');
  }

  /**
   * Get submissions by status
   */
  async getByStatus(status: FormSubmission['status']): Promise<FormSubmission[]> {
    return this.db.form_submissions
      .where('status')
      .equals(status)
      .reverse()
      .sortBy('submittedAt');
  }

  /**
   * Get pending approvals
   */
  async getPendingApprovals(): Promise<FormSubmission[]> {
    return this.getByStatus('submitted');
  }

  /**
   * Get user drafts
   */
  async getUserDrafts(userId: string): Promise<FormSubmission[]> {
    const allSubmissions = await this.getByUser(userId);
    return allSubmissions.filter(s => s.status === 'draft');
  }

  /**
   * Filter submissions
   */
  async filter(filters: FormSubmissionFilters): Promise<FormSubmission[]> {
    let submissions = await this.getAll();

    if (filters.status && filters.status.length > 0) {
      submissions = submissions.filter(s => filters.status!.includes(s.status));
    }

    if (filters.formType && filters.formType.length > 0) {
      submissions = submissions.filter(s => filters.formType!.includes(s.formType));
    }

    if (filters.submittedBy && filters.submittedBy.length > 0) {
      submissions = submissions.filter(s => 
        s.submittedBy && filters.submittedBy!.includes(s.submittedBy)
      );
    }

    if (filters.dateFrom) {
      submissions = submissions.filter(s => 
        new Date(s.submittedAt) >= filters.dateFrom!
      );
    }

    if (filters.dateTo) {
      submissions = submissions.filter(s => 
        new Date(s.submittedAt) <= filters.dateTo!
      );
    }

    if (filters.vesselId) {
      submissions = submissions.filter(s => s.vesselId === filters.vesselId);
    }

    return submissions;
  }

  /**
   * Search submissions
   */
  async search(searchTerm: string): Promise<FormSubmission[]> {
    const term = searchTerm.toLowerCase();
    const allSubmissions = await this.getAll();

    return allSubmissions.filter(submission =>
      submission.formTitle.toLowerCase().includes(term) ||
      submission.formType.toLowerCase().includes(term) ||
      JSON.stringify(submission.formData).toLowerCase().includes(term)
    );
  }

  /**
   * Submit form (change status from draft to submitted)
   */
  async submitForm(submissionId: string, userId: string): Promise<FormSubmission> {
    const submission = await this.getById(submissionId);
    if (!submission) {
      throw new Error('Submission not found');
    }

    if (submission.status !== 'draft') {
      throw new Error('Only draft submissions can be submitted');
    }

    return this.update(submissionId, {
      status: 'submitted',
      submittedBy: userId,
      submittedAt: new Date()
    });
  }

  /**
   * Approve form submission
   */
  async approveForm(submissionId: string, userId: string): Promise<FormSubmission> {
    const submission = await this.getById(submissionId);
    if (!submission) {
      throw new Error('Submission not found');
    }

    if (submission.status !== 'submitted') {
      throw new Error('Only submitted forms can be approved');
    }

    return this.update(submissionId, {
      status: 'approved',
      approvedBy: userId,
      approvedAt: new Date()
    });
  }

  /**
   * Reject form submission
   */
  async rejectForm(submissionId: string, userId: string): Promise<FormSubmission> {
    const submission = await this.getById(submissionId);
    if (!submission) {
      throw new Error('Submission not found');
    }

    if (submission.status !== 'submitted') {
      throw new Error('Only submitted forms can be rejected');
    }

    return this.update(submissionId, {
      status: 'rejected',
      approvedBy: userId,
      approvedAt: new Date()
    });
  }

  /**
   * Archive form submission
   */
  async archiveForm(submissionId: string): Promise<FormSubmission> {
    return this.update(submissionId, {
      status: 'archived'
    });
  }

  /**
   * Get submissions by date range
   */
  async getByDateRange(startDate: Date, endDate: Date): Promise<FormSubmission[]> {
    const allSubmissions = await this.getAll();
    
    return allSubmissions.filter(s => {
      const submittedDate = new Date(s.submittedAt);
      return submittedDate >= startDate && submittedDate <= endDate;
    });
  }

  /**
   * Get submissions by form template
   */
  async getByTemplate(templateId: string): Promise<FormSubmission[]> {
    return this.db.form_submissions
      .where('formTemplateId')
      .equals(templateId)
      .reverse()
      .sortBy('submittedAt');
  }

  /**
   * Get submission statistics
   */
  async getStatistics(organizationId: string) {
    const submissions = await this.getByOrganization(organizationId);

    const total = submissions.length;
    const byStatus = {
      draft: submissions.filter(s => s.status === 'draft').length,
      submitted: submissions.filter(s => s.status === 'submitted').length,
      approved: submissions.filter(s => s.status === 'approved').length,
      rejected: submissions.filter(s => s.status === 'rejected').length,
      archived: submissions.filter(s => s.status === 'archived').length
    };

    const byFormType: Record<string, number> = {};
    submissions.forEach(s => {
      byFormType[s.formType] = (byFormType[s.formType] || 0) + 1;
    });

    const pendingApproval = byStatus.submitted;
    const approvalRate = (byStatus.approved + byStatus.rejected) > 0
      ? Math.round((byStatus.approved / (byStatus.approved + byStatus.rejected)) * 100)
      : 0;

    // Calculate this month's submissions
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonth = submissions.filter(s => 
      new Date(s.submittedAt) >= firstDayOfMonth
    ).length;

    return {
      total,
      byStatus,
      byFormType,
      pendingApproval,
      approvalRate,
      thisMonth
    };
  }

  /**
   * Increment version number
   */
  async incrementVersion(submissionId: string): Promise<FormSubmission> {
    const submission = await this.getById(submissionId);
    if (!submission) {
      throw new Error('Submission not found');
    }

    return this.update(submissionId, {
      version: submission.version + 1
    });
  }

  /**
   * Add attachment to submission
   */
  async addAttachment(submissionId: string, attachmentUrl: string): Promise<FormSubmission> {
    const submission = await this.getById(submissionId);
    if (!submission) {
      throw new Error('Submission not found');
    }

    const attachments = submission.attachments || [];
    attachments.push(attachmentUrl);

    return this.update(submissionId, {
      attachments
    });
  }

  /**
   * Remove attachment from submission
   */
  async removeAttachment(submissionId: string, attachmentUrl: string): Promise<FormSubmission> {
    const submission = await this.getById(submissionId);
    if (!submission) {
      throw new Error('Submission not found');
    }

    const attachments = (submission.attachments || []).filter(url => url !== attachmentUrl);

    return this.update(submissionId, {
      attachments
    });
  }

  /**
   * Update form data
   */
  async updateFormData(
    submissionId: string,
    formData: Record<string, any>
  ): Promise<FormSubmission> {
    return this.update(submissionId, {
      formData,
      updatedAt: new Date()
    });
  }

  /**
   * Get recent submissions (last 7 days)
   */
  async getRecentSubmissions(limit: number = 10): Promise<FormSubmission[]> {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const allSubmissions = await this.getAll();
    const recent = allSubmissions
      .filter(s => new Date(s.submittedAt) >= sevenDaysAgo)
      .sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());

    return recent.slice(0, limit);
  }
}

// Export singleton instances
export const formRepository = new FormRepository();
export const formSubmissionRepository = new FormSubmissionRepository();


